import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Plus, Trash2, Pencil, Save } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { image_url: "", title: "", subtitle: "", cta_text: "", cta_link: "", order: 0, active: true };

export default function AdminSlides() {
  const [slides, setSlides] = useState([]);
  const [editing, setEditing] = useState(null); // slide id or "NEW"
  const [form, setForm] = useState(EMPTY);

  const refresh = () => api.get("/slides/all").then((r) => setSlides(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("NEW"); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditing(s.id); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, order: parseInt(form.order || 0) };
      if (editing === "NEW") await api.post("/slides", payload);
      else await api.patch(`/slides/${editing}`, payload);
      toast.success("Slide salvo");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir slide?")) return;
    try { await api.delete(`/slides/${id}`); toast.success("Excluído"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Slides da Home</h1>
          <p className="text-stone-500 text-sm mt-1">Gerencie o carrossel principal da landing page.</p>
        </div>
        <Button onClick={openNew} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="new-slide-btn">
          <Plus size={16} className="mr-1.5"/> Novo slide
        </Button>
      </div>

      {editing && (
        <Card className="mb-6 p-6 rounded-2xl border-stone-200">
          <h2 className="font-display text-xl font-semibold mb-4">{editing === "NEW" ? "Novo slide" : "Editar slide"}</h2>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>URL da imagem (paisagem 16:9 ou wider)</Label><Input required value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="https://..." className="rounded-xl mt-1" data-testid="sf-url"/></div>
            <div className="sm:col-span-2"><Label>Título</Label><Input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-xl mt-1" data-testid="sf-title"/></div>
            <div className="sm:col-span-2"><Label>Subtítulo</Label><Input value={form.subtitle} onChange={(e) => setForm({...form, subtitle: e.target.value})} className="rounded-xl mt-1" data-testid="sf-subtitle"/></div>
            <div><Label>Texto do botão CTA</Label><Input value={form.cta_text || ""} onChange={(e) => setForm({...form, cta_text: e.target.value})} placeholder="Explorar agora" className="rounded-xl mt-1" data-testid="sf-cta-text"/></div>
            <div><Label>Link do CTA</Label><Input value={form.cta_link || ""} onChange={(e) => setForm({...form, cta_link: e.target.value})} placeholder="/discover/aracaju" className="rounded-xl mt-1" data-testid="sf-cta-link"/></div>
            <div><Label>Ordem</Label><Input type="number" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} className="rounded-xl mt-1" data-testid="sf-order"/></div>
            <div className="flex items-end gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({...form, active: v})} data-testid="sf-active"/><Label>Ativo</Label></div>
            <div className="sm:col-span-2 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="bg-blue-900 hover:bg-blue-950 text-white" data-testid="sf-submit"><Save size={14} className="mr-1.5"/> Salvar</Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {slides.length === 0 && <p className="text-stone-500">Nenhum slide cadastrado.</p>}
        {slides.map((s) => (
          <Card key={s.id} className="overflow-hidden rounded-2xl border-stone-200" data-testid={`slide-card-${s.id}`}>
            <div className="aspect-video bg-stone-100 relative">
              {s.image_url && <img src={s.image_url} alt={s.title} className="absolute inset-0 w-full h-full object-cover"/>}
              <div className="absolute inset-0 bg-gradient-to-t from-blue-950/80 to-transparent"/>
              <div className="absolute bottom-2 left-3 right-3 text-white">
                <div className="font-display font-semibold text-sm leading-tight line-clamp-2">{s.title}</div>
                <div className="text-[10px] text-white/80">#{s.order} · {s.active ? "Ativo" : "Inativo"}</div>
              </div>
            </div>
            <div className="p-3 flex items-center justify-between gap-2">
              <p className="text-xs text-stone-500 truncate flex-1">{s.subtitle}</p>
              <button onClick={() => openEdit(s)} className="text-stone-500 hover:text-stone-900" aria-label="Editar" data-testid={`edit-slide-${s.id}`}><Pencil size={15}/></button>
              <button onClick={() => remove(s.id)} className="text-red-500 hover:text-red-700" aria-label="Excluir" data-testid={`delete-slide-${s.id}`}><Trash2 size={15}/></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
